import { z } from 'zod';
import { idSchema } from './common.js';
import { heartbeatHealthSchema } from './scheduled-jobs.js';
/**
 * Settings → System Health (TS-1039): what the operator screen shows and the vocabulary the
 * API, the worker and the web app share for it.
 */
/** The processes that report breakers and take a runtime log level. */
export const SERVICES = ['api', 'worker'];
export const serviceSchema = z.enum(SERVICES);
/** The levels an operator can pick. `trace`/`fatal`/`silent` stay deploy-time settings. */
export const LOG_LEVELS = ['debug', 'info', 'warn', 'error'];
export const logLevelSchema = z.enum(LOG_LEVELS);
export const breakerStateSchema = z.enum(['closed', 'open', 'half-open']);
/** Breaker names for webhook targets: one per subscription, so one tenant's failing URL never trips another's. */
export const WEBHOOK_BREAKER_PREFIX = 'webhook:';
export const webhookBreakerName = (subscriptionId) => `${WEBHOOK_BREAKER_PREFIX}${subscriptionId}`;
/**
 * Every external dependency, and what the product does while its breaker is open — the
 * "fallback behaviour" the screen shows. Listed here, not discovered, so a dependency that has
 * not been called since the last deploy still appears (as closed, no calls) instead of being
 * missing. Keep each `fallback` true to the code behind it.
 */
export const DEPENDENCIES = {
    cognito: {
        label: 'Cognito (identity provider)',
        services: ['api'],
        fallback: 'Deactivating a user still completes: the CRM rejects their tokens itself. Only the identity-provider sign-out is skipped, and logged.',
    },
    s3: {
        label: 'S3 (document storage)',
        services: ['api', 'worker'],
        fallback: 'Confirming an upload fails and the document stays pending until it is confirmed again. Resume text extraction retries on later runs, then marks the document failed.',
    },
    sqs: {
        label: 'SQS (event queue)',
        services: ['worker'],
        fallback: 'Outbox events stay unpublished and are re-sent on a later drain; nothing is lost. Webhook fan-out waits with them, because it runs after the queue accepts a batch.',
    },
    recruitcrm: {
        label: 'RecruitCRM',
        services: ['api', 'worker'],
        // TS-1835: the sync processor rethrows the breaker's fast failure; RECRUITCRM_JOB_OPTIONS gives
        // each job 5 attempts from 10 s (about 2.5 minutes) before it is dead-lettered.
        // TS-1858: the worker's webhook inbox reads a record back for a delivery without custom fields;
        // a failed read is retried with backoff for 24 hours (`RCRM_INBOX_RETRY_WINDOW_MS`), then dead.
        fallback: 'Calls fail fast instead of waiting. Sync jobs retry with backoff (5 attempts over about 2.5 minutes) and are then dead-lettered; retry them from the dead-letter list once this closes — each re-reads the CRM record, so nothing is lost. Stored RecruitCRM webhooks that need their record read back wait and retry for up to 24 hours. A resume that is only a RecruitCRM link does not open in the viewer; its Download still opens the link.',
    },
    twilio: {
        label: 'Twilio (text replies)',
        services: ['api'],
        // TS-1904: an open breaker means Twilio is not called; the reply is kept as failed, with why.
        fallback: 'Replying to a text from the Messages tab fails at once with "Twilio is not answering. Nothing was sent"; the attempt shows in the conversation as failed, and sending again later is safe. Reading messages is unaffected.',
    },
    'twilio-secret': {
        label: 'Twilio credentials (Secrets Manager)',
        services: ['api'],
        // TS-1904: the API reads `crm-<env>/twilio` itself; a failed read is kept for a minute.
        fallback: 'Replying to texts shows as not set up, with the reason, until the secret can be read again (tried once a minute). Nothing is sent meanwhile. Reading messages is unaffected.',
    },
    stripe: {
        label: 'Stripe',
        services: ['api', 'worker'],
        fallback: 'Calls fail fast instead of waiting. Queued jobs retry with backoff and are dead-lettered after their last attempt.',
    },
    quickbooks: {
        label: 'QuickBooks',
        services: ['api', 'worker'],
        fallback: 'Calls fail fast instead of waiting. Queued jobs retry with backoff and are dead-lettered after their last attempt.',
    },
    slack: {
        label: 'Slack',
        services: ['api', 'worker'],
        fallback: 'Calls fail fast instead of waiting. Queued jobs retry with backoff and are dead-lettered after their last attempt.',
    },
};
export const DEPENDENCY_NAMES = Object.keys(DEPENDENCIES);
export const WEBHOOK_BREAKER_FALLBACK = 'Deliveries to this URL are not attempted while open. They stay queued and are retried with backoff for up to 24 hours after their first attempt.';
/** An automation's receiver (`trigger-http:<organisation>:<host>`): `TRIGGER_RUN_JOB_OPTIONS` retries its sends. */
export const AUTOMATION_BREAKER_FALLBACK = 'Requests to this host are not sent while open. Each send is retried with backoff, 8 attempts over about an hour; after the last one the run is dead, its task shows Failed, and it can be retried from the dead-letter list.';
/** Rolling-window counts from opossum (the last ~10 seconds), summed across instances. */
export const breakerStatsSchema = z.object({
    successes: z.number().int(),
    failures: z.number().int(),
    timeouts: z.number().int(),
    rejects: z.number().int(),
    fallbacks: z.number().int(),
});
export const breakerViewSchema = z.object({
    name: z.string(),
    label: z.string(),
    kind: z.enum(['dependency', 'webhook', 'automation']),
    /** The worst state any live instance reports: open > half-open > closed. */
    state: breakerStateSchema,
    /** When that state began (on the instance reporting it); null before any call. */
    since: z.iso.datetime({ offset: true }).nullable(),
    fallback: z.string(),
    stats: breakerStatsSchema,
    instances: z.array(z.object({ service: serviceSchema, instanceId: z.string(), state: breakerStateSchema })),
    /** Webhook targets: which subscription, so the screen can link to it. */
    subscriptionId: idSchema.nullable(),
    reportedAt: z.iso.datetime({ offset: true }).nullable(),
});
export const queueViewSchema = z.object({
    name: z.string(),
    waiting: z.number().int(),
    active: z.number().int(),
    delayed: z.number().int(),
    failed: z.number().int(),
    completed: z.number().int(),
});
export const scheduledJobViewSchema = z.object({
    job: z.string(),
    description: z.string(),
    expectedEverySeconds: z.number().int(),
    idleIsHealthy: z.boolean(),
    /** No heartbeat yet is fine: fed from outside and not set up in every environment (TS-1892). */
    optional: z.boolean(),
    health: heartbeatHealthSchema,
    lastRunAt: z.iso.datetime({ offset: true }).nullable(),
    lastStatus: z.string().nullable(),
    lastProcessed: z.number().int().nullable(),
    lastError: z.string().nullable(),
    lastWorkAt: z.iso.datetime({ offset: true }).nullable(),
    /** The message feed's open set-aside rows (0 for every other job): the screen offers to review them. */
    setAside: z.number().int(),
});
/** Mail and message sync: what other systems post to `POST /activities` (TS-955), per source. */
export const syncSourceViewSchema = z.object({
    source: z.string(),
    lastReceivedAt: z.iso.datetime({ offset: true }),
    lastOccurredAt: z.iso.datetime({ offset: true }),
    received24h: z.number().int(),
    unmatched: z.number().int(),
});
export const logLevelViewSchema = z.object({
    service: serviceSchema,
    /**
     * What the service runs at now, where the API can know it: its own instance's level. null
     * for another process, which runs at `override` if one is set, else its deploy-time level.
     */
    level: z.string().nullable(),
    override: logLevelSchema.nullable(),
    /** The deploy-time LOG_LEVEL, where known (the API's own). */
    configured: z.string().nullable(),
    updatedAt: z.iso.datetime({ offset: true }).nullable(),
    updatedBy: z.string().nullable(),
    /** How long a change takes to reach every instance of the service. */
    refreshSeconds: z.number().int(),
});
export const systemHealthSchema = z.object({
    checkedAt: z.iso.datetime({ offset: true }),
    /** One field an uptime check can poll: no breaker open, no job unhealthy, nothing dead-lettered. */
    healthy: z.boolean(),
    breakers: z.array(breakerViewSchema),
    /** null when the API has no Redis (REDIS_URL unset): the queues cannot be read. */
    queues: z.array(queueViewSchema).nullable(),
    deadLettered: z.number().int().nullable(),
    jobs: z.array(scheduledJobViewSchema),
    sync: z.array(syncSourceViewSchema),
    logLevels: z.array(logLevelViewSchema),
});
/** How often each service re-reads its override; the worker and the API default to the same. */
export const DEFAULT_LOG_LEVEL_REFRESH_MS = 30_000;
/** A breaker report older than this is from an instance that has stopped (or a deploy ago). */
export const BREAKER_REPORT_STALE_MS = 60_000;
/** A dead-lettered job as the screen lists it (the queue's own id plus what died and why). */
export const deadLetterEntrySchema = z.object({
    id: z.string(),
    queue: z.string(),
    name: z.string(),
    attemptsMade: z.number().int(),
    failedReason: z.string(),
    failedAt: z.iso.datetime({ offset: true }),
    originalJobId: z.string().nullable(),
    data: z.unknown(),
});
export const setLogLevelSchema = z.object({
    /** null clears the override: the service goes back to its deploy-time LOG_LEVEL. */
    level: logLevelSchema.nullable(),
});
//# sourceMappingURL=system-health.js.map